import React, { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Target } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
// removed Label in favor of FormLabel
import { AdminFoundersKeys } from "@/features/founders/api/admin-founders";
import {
  getLicenseeProgression,
  LevelsKeys,
  updateLicenseeProgression,
  UpdateLicenseeProgressionDto,
} from "@/features/founders/api/levels";
import { useToast } from "@/hooks/use-toast";

interface UpdateProgressionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  licenseeId: string | null;
  licenseeName: string;
  currentProgression?: {
    hasSharedDemo?: boolean;
    hasCompletedPlaybook?: boolean;
    nicheDefined?: boolean;
  };
}

const UpdateProgressionDialog: React.FC<UpdateProgressionDialogProps> = ({
  open,
  onOpenChange,
  licenseeId,
  licenseeName,
  currentProgression = {},
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<UpdateLicenseeProgressionDto>({
    defaultValues: {
      hasSharedDemo: false,
      hasCompletedPlaybook: false,
      nicheDefined: false,
    },
  });

  // Fetch current progression from new levels endpoint
  const { data: progressionData } = useQuery({
    queryKey: [LevelsKeys.GET_LICENSEE_PROGRESSION, licenseeId],
    queryFn: () => getLicenseeProgression(licenseeId as string),
    enabled: open && !!licenseeId,
  });

  // Initialize from server when available; otherwise from fallback props.
  // Do not overwrite once the user started editing (dirty).
  useEffect(() => {
    if (!open || !licenseeId) return;

    if (progressionData) {
      if (!form.formState.isDirty) {
        form.reset({
          hasSharedDemo: !!progressionData.demoPublished,
          hasCompletedPlaybook: !!progressionData.courseCompleted,
          nicheDefined: !!progressionData.nicheDefined,
        });
      }
      return;
    }

    if (!form.formState.isDirty) {
      form.reset({
        hasSharedDemo: !!currentProgression?.hasSharedDemo,
        hasCompletedPlaybook: !!currentProgression?.hasCompletedPlaybook,
        nicheDefined: !!currentProgression?.nicheDefined,
      });
    }
  }, [open, licenseeId, progressionData]);

  const { mutate: updateProgression, isPending } = useMutation({
    mutationFn: (data: UpdateLicenseeProgressionDto) => {
      if (!licenseeId) throw new Error("No licensee ID provided");
      return updateLicenseeProgression(licenseeId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDER, licenseeId],
      });
      queryClient.invalidateQueries({
        queryKey: [LevelsKeys.GET_LICENSEE_PROGRESSION, licenseeId],
      });
      queryClient.invalidateQueries({
        queryKey: [LevelsKeys.GET_STARTER_PROGRESS],
      });
      toast({
        title: "Success",
        description: "Licensee progression updated successfully",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update progression",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    updateProgression(values);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Update Progression
          </DialogTitle>
          <DialogDescription>
            Update the progression milestones for {licenseeName}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="grid gap-4 py-4">
            <FormField
              control={form.control}
              name="nicheDefined"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      id="nicheDefined"
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="nicheDefined"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer mt-0"
                  >
                    Niche Defined
                  </FormLabel>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasSharedDemo"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      id="demo"
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="demo"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Has Shared Demo
                  </FormLabel>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasCompletedPlaybook"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox
                      id="playbook"
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="playbook"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Has Completed Playbook
                  </FormLabel>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateProgressionDialog;
